// src/features/dashboard/DashboardContainer.jsx
import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { useAuth } from '@/core/contexts/AuthContext';
import { ROUTES } from '@/core/constants/routes';
import RoleDashboardLoader from '@/features/dashboard/RoleDashboardLoader';
import PostLoginAgreementModal from '@/features/auth/components/PostLoginAgreementModal';
import adminService from '@/services/adminService';
import { deriveOnboardingRequired, isAccreditedDeclarationAccepted } from '@/services/authService';
import PushPermissionPrompt from '@/components/notifications/PushPermissionPrompt';

const FirstLoginOnboardingPage = lazy(() => import('@/features/auth/onboarding/FirstLoginOnboardingPage'));

/**
 * Main dashboard container that routes to role-specific dashboards
 * based on the authenticated user's role.
 */
const DashboardContainer = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [checkingAgreement, setCheckingAgreement] = useState(true);
  // Prevent the billing modal from re-opening in the same session (legacy gate;
  // deferred firms no longer set requires_billing_selection).
  const billingModalHandledRef = React.useRef(false);

  const handleAgreementComplete = useCallback(() => {
    setShowAgreementModal(false);
  }, []);

  useEffect(() => {
    const loadPlatformSettings = async () => {
      if (!user) {
        setCheckingAgreement(false);
        return;
      }

      try {
        const response = await adminService.getPlatformFeatures();
        const globalSettings = response?.data?.global_settings || {};
        setPlatformSettings(globalSettings);
      } catch (loadError) {
        console.error('Error loading platform settings:', loadError);
        setPlatformSettings({});
      } finally {
        setCheckingAgreement(false);
      }
    };

    loadPlatformSettings();
  }, [user]);

  useEffect(() => {
    if (checkingAgreement || !user) return;
    const needsOnboarding = deriveOnboardingRequired(user);
    if (needsOnboarding && !location.pathname.startsWith(ROUTES.ONBOARDING)) {
      navigate(ROUTES.ONBOARDING, { replace: true });
    }
  }, [checkingAgreement, user, location.pathname, navigate]);

  useEffect(() => {
    if (
      !checkingAgreement &&
      user &&
      platformSettings &&
      !showAgreementModal &&
      !deriveOnboardingRequired(user)
    ) {
      const requireAgreements = platformSettings.require_agreement_acceptance?.enabled;
      const userNeedsToAccept = user.agreement_accepted === false ||
        user.agreement_accepted === null ||
        user.agreement_accepted === undefined;

      const role = String(user.role || user.user_type || '').toLowerCase();
      const investorNeedsTcOnly =
        role === 'investor' &&
        isAccreditedDeclarationAccepted(user) &&
        userNeedsToAccept;

      if (investorNeedsTcOnly || (requireAgreements && userNeedsToAccept)) {
        setShowAgreementModal(true);
        return;
      }

      const approvalPending =
        user.pending_admin_review === true ||
        String(user.approval_status || '').toLowerCase() === 'pending';

      if (user.requires_billing_selection === true && !approvalPending && !billingModalHandledRef.current) {
        billingModalHandledRef.current = true;
        setShowAgreementModal(true);
      }
    }
  }, [user, platformSettings, checkingAgreement, showAgreementModal]);

  useEffect(() => {
    if (
      location.pathname === ROUTES.DASHBOARD &&
      user?.role &&
      !deriveOnboardingRequired(user)
    ) {
      navigate(ROUTES.getDashboardForRole(user.role), { replace: true });
    }
  }, [location.pathname, user, navigate]);

  if (checkingAgreement) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: theme.palette.background.default
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return (
    <>
      <PushPermissionPrompt />
      {user && !deriveOnboardingRequired(user) && (
        <PostLoginAgreementModal
          open={showAgreementModal}
          onComplete={handleAgreementComplete}
        />
      )}
      <Box
        component="main"
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          minHeight: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Routes>
          <Route
            path="onboarding/*"
            element={(
              <Suspense
                fallback={(
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                    <CircularProgress />
                  </Box>
                )}
              >
                <FirstLoginOnboardingPage />
              </Suspense>
            )}
          />

          <Route
            path="manager/*"
            element={
              user.role === 'manager' ? (
                <RoleDashboardLoader />
              ) : (
                <Navigate to={ROUTES.DASHBOARD} replace />
              )
            }
          />

          <Route
            path="investor/*"
            element={
              user.role === 'investor' ? (
                <RoleDashboardLoader />
              ) : (
                <Navigate to={ROUTES.DASHBOARD} replace />
              )
            }
          />

          <Route
            path="admin/*"
            element={
              user.role === 'admin' ? (
                <RoleDashboardLoader />
              ) : (
                <Navigate to={ROUTES.DASHBOARD} replace />
              )
            }
          />

          <Route
            path="*"
            element={(
              <Navigate
                to={
                  deriveOnboardingRequired(user)
                    ? ROUTES.ONBOARDING
                    : ROUTES.getDashboardForRole(user.role)
                }
                replace
              />
            )}
          />
        </Routes>
      </Box>
    </>
  );
};

export default DashboardContainer;
